import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { KeycloakSetup } from '../pages/login/utils/keycloak-setup';
import { TokenService } from '../pages/login/services/token.service';
import { AppSessionStorageService } from '../services/app-session-storage.service';
import { environment } from 'src/environments/environment';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root',
})
export class InterceptorService implements HttpInterceptor {
  private readonly sessionStorage = inject(AppSessionStorageService);
  private readonly tokenService = inject(TokenService);
  private readonly oauth = inject(OAuthService);

  /**
   * Metodo que se ejecuta con cada petición HTTP
   * Agregaremos la información necesaria del usuario en los Headers
   * @param req
   * @param next
   */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // No inyectar AuthService aquí: el discovery de Keycloak pasa por este
    // interceptor y crearía ciclo KeycloakSetup -> HTTP -> Auth -> KeycloakSetup.
    const user = this.sessionStorage.getUserLoginFromStorage();

    // Verificar si la solicitud ya tiene la cabecera X-Security-Token
    if (req.headers.has('X-Security-Token')) {
      // Si ya tiene la cabecera X-Security-Token, no modificar las cabeceras
      return next.handle(req);
    }

    const customHeaders = environment.keycloak_login
      ? { Authorization: `Bearer ${this.oauth.getAccessToken()}` }
      : { 'session-id': this.tokenService.getToken() };

    if (
      req.url.includes(KeycloakSetup.issuer) ||
      req.headers.has('X-Security-Token')
    ) {
      return next.handle(req);
    }

    // Detectar si es una petición con FormData (el body es instancia de FormData)
    if (req.body instanceof FormData) {
      const headers = new HttpHeaders({
        user_login: JSON.stringify(user) || '',
        ...customHeaders,
      });
      const reqClone = req.clone({
        headers,
      });
      return next.handle(reqClone);
    }

    if (req.headers.get('Content-Type') === 'multipart/form-data') {
      const headers = new HttpHeaders({
        user_login: JSON.stringify(user) || '',
        ...customHeaders,
      });
      const reqClone = req.clone({
        headers,
      });
      return next.handle(reqClone);
    }

    const headers = new HttpHeaders({
      user_login: JSON.stringify(user) || '',
      'Content-Type': 'application/json',
      accept: 'application/json',
      ...customHeaders,
    });
    const reqClone = req.clone({
      headers,
    });

    return next.handle(reqClone).pipe(
      catchError(err => {
        if (environment.keycloak_login && err?.status === 401) {
          // Intentar refrescar y reintentamos sólo una vez
          return from(this.oauth.refreshToken()).pipe(
            switchMap(() => {
              const retryHeaders = reqClone.headers.set(
                'Authorization',
                `Bearer ${this.oauth.getAccessToken()}`
              );
              const retried = req.clone({ headers: retryHeaders });
              return next.handle(retried);
            }),
            catchError(refreshErr => {
              // Si falla el refresh, propagamos el error original
              return throwError(() => err);
            })
          );
        }
        return throwError(() => err);
      })
    );
  }
}
